import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@material-ui/core/styles';
import {
  Tooltip,
  Typography,
  IconButton,
  Divider,
  Chip,
  Paper,
  Popper,
  MenuList,
  MenuItem,
  ClickAwayListener,
} from '@material-ui/core';
import { Check, Delete } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteEvaluation,
  confirmEvaluation,
  setActionToConfirm,
  setShownQuality,
  setShownDensity,
  setShownMicrocalc,
  setShownOpacities,
  setOpacitiesOverlaysToShow,
  setMicrocalcOverlaysToShow,
} from '../../../../../store';
import { reviewerName, getCurrentModuleStatus } from './SummaryCommon';

// Styles for the ReviewerSelector component
const reviewerSelectorStyles = {
  menu_list_item: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr ',
    padding: '1px',
    borderRadius: '5px',
  },
  list_item_text: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px',
    margin: '2px 6px',
    borderRadius: '5px',
  },
};

export const useReviewerSelector = (type) => {
  const [reviewerTooltip, setReviewerTooltip] = useState(false);
  const [popperOpen, setPopperOpen] = useState(false);
  const anchorRef = useRef(null);

  const handlePopperToggle = () => {
    setReviewerTooltip(false);
    setPopperOpen((prevOpen) => !prevOpen);
  };

  const clickAwayHandler = () => {
    setReviewerTooltip(false);
    setPopperOpen(false);
  };

  return {
    reviewerTooltip,
    setReviewerTooltip,
    popperOpen,
    setPopperOpen,
    anchorRef,
    handlePopperToggle,
    clickAwayHandler,
  };
};

export const ReviewerSelector = ({
  type,
  shownContent,
  appliedContent,
  isConfirmed,
  isRelabeled,
  classes,
  style,
  onPopperClose,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const results = useSelector((state) => state.results);
  const { premium_features } = useSelector((store) => store.global_settings || {});
  const { data_annotation } = premium_features;
  const annotatorRole = data_annotation && localStorage.getItem('annotation') === 'true';
  const {
    applied_opacities,
    applied_microcalc,
    confirmed_opacities,
    confirmed_microcalc,
    shown_microcalc,
    shown_opacities,
  } = useSelector((state) => state.results);
  const currentOpacitiesOverlays = useSelector((state) => state.images.opacitiesOverlaysToShow);
  const currentMicrocalcOverlays = useSelector((state) => state.images.microcalcOverlaysToShow);
  const isMicrocalcConfirmed = applied_microcalc === 'bbox' && confirmed_microcalc;
  const isMicrocalcRelabeled = confirmed_microcalc && applied_microcalc !== 'bbox';
  const isOpacityConfirmed = applied_opacities === 'bbox' && confirmed_opacities;
  const isOpacityRelabeled = confirmed_opacities && applied_opacities !== 'bbox';

  const {
    reviewerTooltip,
    setReviewerTooltip,
    popperOpen,
    setPopperOpen,
    anchorRef,
    handlePopperToggle,
    clickAwayHandler,
  } = useReviewerSelector(type);

  // Get module status info
  const getModuleStatusInfo = () => {
    return getCurrentModuleStatus(appliedContent, shownContent, isConfirmed, isRelabeled, type, theme, t);
  };

  // Generate badge content
  const renderStatusBadgeContent = () => {
    const moduleStatus = getModuleStatusInfo();
    if (type === 'diagnostics') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {(isMicrocalcConfirmed || isOpacityConfirmed || isRelabeled) && moduleStatus.fabIcon}{' '}
          {reviewerName(shownContent)} {isMicrocalcConfirmed ? 'Calc' : ''}
          {isMicrocalcConfirmed && isOpacityConfirmed && ' & '}
          {isOpacityConfirmed ? 'Opa' : ''}
        </div>
      );
    } else if ((type !== 'diagnostics' && isConfirmed) || isRelabeled) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {moduleStatus.fabIcon} {reviewerName(shownContent)}
        </div>
      );
    }

    return reviewerName(shownContent);
  };

  // Handlers for ReviewSelect functionality
  const onDelete = (created_by) => {
    dispatch(
      setActionToConfirm({
        action: () => dispatch(deleteEvaluation(type, created_by, t)),
        text: `${t('revision.delete_eval', {
          user: `${created_by}`,
        })}`,
      })
    );
    if (onPopperClose) {
      onPopperClose();
    }
    clickAwayHandler();
  };

  const onConfirmReviewer = (created_by) => {
    if (type === 'diagnostics') {
      const microcalcState = !results[`confirmed_microcalc`] || created_by !== results[`applied_microcalc`];
      const opacitiesState = !results[`confirmed_opacities`] || created_by !== results[`applied_opacities`];

      if (microcalcState && !opacitiesState) {
        dispatch(confirmEvaluation(created_by, 'opacities', opacitiesState, t));
      }
      if (opacitiesState && !microcalcState) {
        dispatch(confirmEvaluation(created_by, 'microcalc', microcalcState, t));
      }
      if ((microcalcState && opacitiesState) || (!microcalcState && !opacitiesState)) {
        dispatch(confirmEvaluation(created_by, 'microcalc', microcalcState, t));
        setTimeout(() => {
          dispatch(confirmEvaluation(created_by, 'opacities', opacitiesState, t));
        }, 3000);
      }
    } else {
      const state = !results[`confirmed_${type}`] || created_by !== results[`applied_${type}`];
      dispatch(confirmEvaluation(created_by, type, state, t));
    }

    if (onPopperClose) {
      onPopperClose();
    }
    clickAwayHandler();
  };

  const checkTooltip = (currentRow) => {
    let confirmedType, appliedType;

    if (type === 'diagnostics') {
      // For diagnostics, check both microcalc and opacities
      const confirmedMicrocalc = results?.confirmed_microcalc;
      const confirmedOpac = results?.confirmed_opacities;

      confirmedType =
        results.confirmed_microcalc === currentRow || results.confirmed_opacities === currentRow;
      appliedType = results.applied_microcalc === currentRow || results.applied_opacities === currentRow;

      if (!confirmedMicrocalc && !confirmedOpac) {
        return t('ResultView.label_accept_icon');
      } else if (appliedType && !confirmedType && currentRow === 'bbox') {
        if ((confirmedMicrocalc && confirmedMicrocalc !== currentRow) || confirmedOpac !== currentRow) {
          return t('ResultView.label_accept_icon');
        }
        return t('ResultView.label_discard_icon');
      }
      if (confirmedType || appliedType) {
        return t('ResultView.label_discard_icon');
      } else return t('ResultView.label_accept_icon');
    } else {
      confirmedType = results[`confirmed_${type}`];
      appliedType = results[`applied_${type}`];

      if (appliedType === currentRow && !confirmedType) {
        return t('ResultView.label_accept_icon');
      } else if (currentRow === appliedType) {
        return t('ResultView.label_discard_icon');
      } else if (currentRow !== appliedType) {
        return t('ResultView.label_accept_icon');
      }
    }
  };

  const changeShownResults = (created_by, currentModule, close = false) => {
    if (close) {
      if (onPopperClose) {
        onPopperClose();
      }
      clickAwayHandler();
    }
    if (currentModule === 'quality') dispatch(setShownQuality(created_by));
    else if (currentModule === 'density') dispatch(setShownDensity(created_by));
    else if (currentModule === 'diagnostics') {
      dispatch(setShownOpacities(created_by));
      dispatch(setOpacitiesOverlaysToShow({ ...currentOpacitiesOverlays }));
      dispatch(setShownMicrocalc(created_by));
      dispatch(setMicrocalcOverlaysToShow({ ...currentMicrocalcOverlays }));
    }
  };

  // Tooltip content
  const tooltipContent = (
    <>
      <Typography className={classes.tooltip_text} color='inherit'>
        {t('ResultView.current_results_shown')}: {reviewerName(shownContent)}
      </Typography>
      <Divider style={{ margin: '6px 0' }} />
      <Typography className={classes.tooltip_text} color='inherit'>
        {t('ResultView.current_applied')}: {reviewerName(appliedContent)}
      </Typography>
      <p>{t('ResultView.change_reviewer')}</p>
    </>
  );

  // Popper content (ReviewSelect functionality)
  const renderPopperContent = () => {
    // Handle diagnostics type specially by combining microcalc and opacities evaluations
    let evaluationsToShow = {};
    if (type === 'diagnostics') {
      const microcalcEvaluations = results?.evaluations?.microcalc || {};
      const opacitiesEvaluations = results?.evaluations?.opacities || {};
      // Combine both evaluation sets, removing duplicates
      evaluationsToShow = { ...microcalcEvaluations, ...opacitiesEvaluations };
    } else {
      evaluationsToShow = results?.evaluations?.[type] || {};
    }

    if (!results || !results.evaluations || Object.keys(evaluationsToShow).length === 0) {
      return null;
    }

    return (
      <MenuList variant='menu' id='reviewer-list'>
        {Object.keys(evaluationsToShow).map((created_by) => (
          <MenuItem
            style={{
              ...reviewerSelectorStyles.menu_list_item,
              fontSize: theme.fonts.responsiveMedium,
              color:
                (type === 'diagnostics'
                  ? results.shown_microcalc === created_by || results.shown_opacities === created_by
                  : results[`shown_${type}`] === created_by) && theme.palette.accents.main,
              fontWeight:
                (type === 'diagnostics'
                  ? results.shown_microcalc === created_by || results.shown_opacities === created_by
                  : results[`shown_${type}`] === created_by) && 'bold',
            }}
            key={created_by}
            onMouseEnter={() => changeShownResults(created_by, type)}
            onClick={() => changeShownResults(created_by, type, true)}>
            <Tooltip
              placement='left-start'
              arrow
              title={
                <Typography className={classes.tooltip_text}>
                  {(
                    type === 'diagnostics'
                      ? results.applied_microcalc === created_by || results.applied_opacities === created_by
                      : results[`applied_${type}`] === created_by
                  )
                    ? t('ResultView.selected_reviewer')
                    : t('ResultView.label_reviewer')}
                </Typography>
              }>
              <span style={reviewerSelectorStyles.list_item_text}>{reviewerName(created_by)}</span>
            </Tooltip>
            {created_by !== 'bbox' && (
              <>
                <Tooltip
                  placement='right-end'
                  arrow
                  title={
                    <Typography className={classes.tooltip_text}>
                      {annotatorRole ? checkTooltip(created_by) : t('UserPanel.no_annotating')}
                    </Typography>
                  }>
                  <span>
                    <IconButton
                      disabled={!annotatorRole}
                      size='medium'
                      onClick={() => {
                        onConfirmReviewer(created_by);
                      }}>
                      <Check
                        fontSize='small'
                        style={{
                          color: (
                            type === 'diagnostics'
                              ? (!!results.confirmed_microcalc && created_by === results.applied_microcalc) ||
                                (!!results.confirmed_opacities && created_by === results.applied_opacities)
                              : !!results[`confirmed_${type}`] && created_by === results[`applied_${type}`]
                          )
                            ? 'rgb(96, 170, 77)'
                            : 'gray',
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  placement='right-end'
                  arrow
                  title={
                    <Typography className={classes.tooltip_text}>
                      {annotatorRole ? t('ResultView.label_delete_icon') : t('UserPanel.no_annotating')}
                    </Typography>
                  }>
                  <span>
                    <IconButton
                      size='medium'
                      disabled={!annotatorRole}
                      onClick={() => {
                        onDelete(created_by);
                      }}>
                      <Delete color='secondary' fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </MenuItem>
        ))}
      </MenuList>
    );
  };

  const moduleStatusInfo = getModuleStatusInfo();

  return (
    <ClickAwayListener onClickAway={clickAwayHandler} mouseEvent='onClick'>
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Tooltip placement='right-end' arrow title={tooltipContent} open={reviewerTooltip}>
          <Chip
            label={renderStatusBadgeContent()}
            variant='outlined'
            onClick={handlePopperToggle}
            size='small'
            onMouseEnter={() => setReviewerTooltip(true)}
            onMouseLeave={() => setReviewerTooltip(false)}
            ref={anchorRef}
            style={{
              backgroundColor: moduleStatusInfo?.color,
              color: isConfirmed || isRelabeled ? 'white' : theme.palette.accents.main,
              minWidth: '120px',
              height: '22px',
              minHeight: '22px',
              fontSize: theme.fonts.responsiveMedium,
              border: `1px solid ${theme.palette.accents.dark}`,
              ...style,
            }}
          />
        </Tooltip>

        <Popper
          open={popperOpen}
          anchorEl={anchorRef.current}
          placement='bottom-start'
          disablePortal={false}
          keepMounted={false}
          modifiers={{
            flip: {
              enabled: true,
            },
            preventOverflow: {
              enabled: true,
              boundariesElement: 'scrollParent',
            },
          }}>
          <Paper className={classes.popperPaper}>{renderPopperContent()}</Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};
